import { Pencil, Plus, ShoppingCart } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormError } from '@/components/ui/form-error';
import { Input } from '@/components/ui/input';
import { useCartItemForm } from '@/hooks/commercial/use-cart-item-form';
import { useSpecificClientPrice } from '@/hooks/commercial/use-specific-client-price';
import { useFormError } from '@/hooks/use-form-error';
import { toast } from '@/hooks/use-toast';
import { formatDataAmount } from '@/lib/formatters';
import {
  type ConnectivityOption,
  type ICatalogItem,
  type IProposalCartItem,
  NEGOTIATION_TYPES,
  NegotiationType,
  type NegotiationTypeOption,
} from '@/types/commercial/cart';
import { SpecificPriceInlineForm } from './specific-price-inline-form';

function getItemLabel(negotiationType: string): string {
  switch (negotiationType) {
    case NegotiationType.SALE_WITH_SIM_CARD:
    case NegotiationType.SALE_WITHOUT_SIM_CARD:
      return 'Produto';
    case NegotiationType.SIM_CARD:
      return 'Conectividade';
    case NegotiationType.ACCESSORY_SALE:
      return 'Acessório';
    case NegotiationType.SERVICES:
      return 'Serviço';
    default:
      return 'Item';
  }
}

function getCatalogItemDisplay(item: ICatalogItem): string {
  if (
    (item.negotiationType === NegotiationType.SALE_WITH_SIM_CARD ||
      item.negotiationType === NegotiationType.SALE_WITHOUT_SIM_CARD) &&
    item.description?.trim()
  ) {
    return `${item.name} (${item.description.trim()})`;
  }
  if (item.negotiationType !== NegotiationType.SIM_CARD || !item.simCard) {
    return item.name;
  }

  return `${item.simCard.carriers.join('/')} - ${formatDataAmount(item.simCard.dataAmountMb)} - ${item.simCard.planType}`;
}

interface StepCartAddItemsDialogProps {
  open: boolean;
  onClose: () => void;
  proposalId?: string;
  accountId?: string;
  editingItem?: IProposalCartItem | null;
}

export function StepCartAddItemsDialog({
  open,
  onClose,
  proposalId,
  accountId,
  editingItem,
}: StepCartAddItemsDialogProps) {
  const { formError, showFormError, clearFormError } = useFormError(open);
  const [showProductSpecificPrice, setShowProductSpecificPrice] = useState(false);
  const [showConnectivitySpecificPrice, setShowConnectivitySpecificPrice] = useState(false);

  const [discountDisplay, setDiscountDisplay] = useState(() =>
    String(editingItem?.discountPercent ?? ''),
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync only when editing item changes
  useEffect(() => {
    if (editingItem) {
      setDiscountDisplay(String(editingItem.discountPercent ?? ''));
    } else {
      setDiscountDisplay('');
    }
  }, [editingItem?.id]);

  const {
    form,
    negotiationType,
    hasNegotiationType,
    isEditMode,
    isSaleWithSimCard,
    isSimCard,
    isServices,
    catalogItems,
    catalogLoading,
    connectivityOptions,
    selectedCatalogItem,
    priceInstallmentDisplay,
    priceUpfrontDisplay,
    priceYearlyDisplay,
    priceFixedDisplay,
    priceMonthlyDisplay,
    connectivityPriceDisplay,
    addMutation,
    updateMutation,
    buildPayload,
    buildUpdatePayload,
    resetForm,
    resetItemFields,
  } = useCartItemForm(proposalId, editingItem);

  const { handleSubmit } = form;
  const isSubmitting = isEditMode ? updateMutation.isPending : addMutation.isPending;
  const itemLabel = getItemLabel(negotiationType);
  const catalogItemId = form.watch('catalogItemId');
  const connectivityId = form.watch('connectivityId');
  const selectedConnectivity = connectivityOptions.find((option) => option.id === connectivityId);
  const serviceSpecificPriceType = selectedCatalogItem?.service?.monthlyPriceInCents
    ? 'monthly'
    : selectedCatalogItem?.service?.yearlyPriceInCents
      ? 'yearly'
      : 'fixed';
  const defaultPriceGridClass =
    isSimCard || (isServices && serviceSpecificPriceType !== 'monthly')
      ? 'grid-cols-1'
      : isServices
        ? 'grid-cols-1'
        : 'grid-cols-2';

  const { existingPrice: existingProductSpecificPrice } = useSpecificClientPrice(
    isEditMode ? accountId : undefined,
    isEditMode ? catalogItemId : undefined,
    isEditMode ? (negotiationType as NegotiationType) : undefined,
  );

  useEffect(() => {
    if (open && isEditMode && existingProductSpecificPrice) {
      const hasProductSpecificPrice =
        existingProductSpecificPrice.priceCashInCents > 0 ||
        existingProductSpecificPrice.priceInstallmentInCents > 0 ||
        (existingProductSpecificPrice.ranges?.length ?? 0) > 0;
      const hasMonthlySpecificPrice = existingProductSpecificPrice.priceMonthlyInCents > 0;

      if (!isSaleWithSimCard || hasProductSpecificPrice) {
        setShowProductSpecificPrice(true);
      }
      if (isSaleWithSimCard && hasMonthlySpecificPrice) {
        setShowConnectivitySpecificPrice(true);
      }
    }
  }, [open, isEditMode, isSaleWithSimCard, existingProductSpecificPrice]);

  const handleClose = useCallback(() => {
    resetForm();
    setShowProductSpecificPrice(false);
    setShowConnectivitySpecificPrice(false);
    setDiscountDisplay('');
    onClose();
  }, [resetForm, onClose]);

  const submitItem = async (onSuccess: () => void) => {
    try {
      if (isEditMode) {
        const payload = buildUpdatePayload();
        if (!payload) return;
        await updateMutation.mutateAsync(payload);
        toast({ variant: 'success', title: 'Item atualizado no carrinho' });
      } else {
        const payload = buildPayload();
        if (!payload) return;
        await addMutation.mutateAsync(payload);
        toast({ variant: 'success', title: 'Item adicionado ao carrinho' });
      }
      onSuccess();
    } catch (error) {
      showFormError(error);
    }
  };

  const handleAdd = handleSubmit(() => submitItem(handleClose));
  const handleAddAndNew = handleSubmit(() => submitItem(resetItemFields));

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 border-b pb-4">
            {isEditMode ? <Pencil className="h-5 w-5" /> : <ShoppingCart className="h-5 w-5" />}
            {isEditMode ? 'Editar item do carrinho' : 'Adicionar itens ao carrinho'}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            noValidate
            onSubmitCapture={clearFormError}
            onSubmit={handleAdd}
            className="space-y-6"
          >
            <FormField
              control={form.control}
              name="negotiationType"
              render={({ field }) => {
                const selected = NEGOTIATION_TYPES.find((t) => t.id === field.value);
                return (
                  <FormItem>
                    <FormLabel>Tipo de negociação</FormLabel>
                    <FormControl>
                      <Combobox<NegotiationTypeOption>
                        data={[...NEGOTIATION_TYPES]}
                        value={selected ? [selected] : []}
                        keyExtractor={(item) => item.id}
                        displayValueGetter={(item) => item.name}
                        placeholder="Selecione"
                        type="single"
                        disabled={isEditMode}
                        onOptionChange={(sel) => {
                          field.onChange(sel.length > 0 ? sel[0].id : '');
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            {hasNegotiationType && (
              <>
                <div className="border-t pt-5">
                  <div className="flex gap-3">
                    <div className="flex-[3]">
                      <FormField
                        control={form.control}
                        name="catalogItemId"
                        render={({ field }) => {
                          const selected = catalogItems.find((item) => item.id === field.value);
                          return (
                            <FormItem>
                              <FormLabel>{itemLabel}*</FormLabel>
                              <FormControl>
                                <Combobox<ICatalogItem>
                                  data={catalogItems}
                                  value={selected ? [selected] : []}
                                  keyExtractor={(item) => item.id}
                                  displayValueGetter={getCatalogItemDisplay}
                                  placeholder="Selecione"
                                  type="single"
                                  isLoading={catalogLoading}
                                  disabled={isEditMode}
                                  onOptionChange={(sel) => {
                                    field.onChange(sel.length > 0 ? sel[0].id : '');
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />
                    </div>

                    <div className="flex-[1]">
                      <FormField
                        control={form.control}
                        name="quantity"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Quantidade*</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                step={1}
                                placeholder="0"
                                value={field.value || ''}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  if (raw === '') {
                                    field.onChange(0);
                                    return;
                                  }
                                  const parsed = Number.parseInt(raw, 10);
                                  if (!Number.isNaN(parsed) && parsed >= 0) {
                                    field.onChange(parsed);
                                  }
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <div className="mt-5 space-y-4">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium">Tabela de preços padrão:</p>
                      {!showProductSpecificPrice && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 rounded-md bg-black px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                          onClick={() => setShowProductSpecificPrice(true)}
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Add Tabela Específica
                        </button>
                      )}
                    </div>

                    <div className={`grid ${defaultPriceGridClass} gap-3`}>
                      {!isSimCard && !isServices && (
                        <div className="space-y-2">
                          <p className="text-sm text-muted-foreground">Preço unitário a prazo</p>
                          <Input value={priceInstallmentDisplay} disabled />
                        </div>
                      )}

                      {!isSimCard && !isServices && (
                        <div className="space-y-2">
                          <p className="text-sm text-muted-foreground">Preço unitário à vista</p>
                          <Input value={priceUpfrontDisplay} disabled />
                        </div>
                      )}

                      {(isSimCard || (isServices && serviceSpecificPriceType === 'monthly')) && (
                        <div className="space-y-2">
                          <p className="text-sm text-muted-foreground">Preço mensal unitário</p>
                          <Input value={priceMonthlyDisplay} disabled />
                        </div>
                      )}

                      {isServices && serviceSpecificPriceType === 'yearly' && (
                        <div className="space-y-2">
                          <p className="text-sm text-muted-foreground">Preço anual unitário</p>
                          <Input value={priceYearlyDisplay} disabled />
                        </div>
                      )}

                      {isServices && serviceSpecificPriceType === 'fixed' && (
                        <div className="space-y-2">
                          <p className="text-sm text-muted-foreground">Preço fixo unitário</p>
                          <Input value={priceFixedDisplay} disabled />
                        </div>
                      )}
                    </div>

                    {isEditMode && (
                      <FormField
                        control={form.control}
                        name="discountPercent"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Desconto (%)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min={0}
                                max={100}
                                step={1}
                                placeholder="0"
                                value={discountDisplay}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  if (raw === '') {
                                    setDiscountDisplay('');
                                    return;
                                  }
                                  const num = Number.parseInt(raw, 10);
                                  if (!Number.isNaN(num)) setDiscountDisplay(String(num));
                                }}
                                onBlur={() => {
                                  const parsed = Number.parseInt(discountDisplay, 10);
                                  const normalized =
                                    discountDisplay === '' || Number.isNaN(parsed)
                                      ? 0
                                      : Math.min(100, Math.max(0, parsed));
                                  field.onChange(normalized);
                                  setDiscountDisplay(String(normalized));
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    {showProductSpecificPrice && catalogItemId && (
                      <SpecificPriceInlineForm
                        type={
                          isSimCard ? 'monthly' : isServices ? serviceSpecificPriceType : 'product'
                        }
                        accountId={accountId}
                        productId={catalogItemId}
                        negotiationType={negotiationType as NegotiationType}
                        proposalId={proposalId}
                        fallbackPriceMonthlyInCents={
                          isSaleWithSimCard
                            ? (selectedConnectivity?.priceInBundleInCents ??
                              selectedCatalogItem?.basePriceMonthlyInCents ??
                              0)
                            : 0
                        }
                        onClose={() => setShowProductSpecificPrice(false)}
                      />
                    )}
                  </div>
                </div>

                {isSaleWithSimCard && (
                  <div className="border-t pt-5">
                    <FormField
                      control={form.control}
                      name="connectivityId"
                      render={({ field }) => {
                        const selected = connectivityOptions.find((c) => c.id === field.value);
                        return (
                          <FormItem>
                            <FormLabel>Conectividade*</FormLabel>
                            <FormControl>
                              <Combobox<ConnectivityOption>
                                data={connectivityOptions}
                                value={selected ? [selected] : []}
                                keyExtractor={(item) => item.id}
                                displayValueGetter={(item) => item.name}
                                placeholder="Selecione"
                                type="single"
                                disabled={isEditMode}
                                onOptionChange={(sel) => {
                                  field.onChange(sel.length > 0 ? sel[0].id : '');
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />

                    <div className="mt-5 space-y-4">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium">Tabela de preços padrão:</p>
                        {!showConnectivitySpecificPrice && catalogItemId && (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1.5 rounded-md bg-black px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                            onClick={() => setShowConnectivitySpecificPrice(true)}
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add Tabela Específica
                          </button>
                        )}
                      </div>

                      <div className="max-w-xs space-y-2">
                        <p className="text-sm text-muted-foreground">Preço mensal unitário</p>
                        <Input value={connectivityPriceDisplay} disabled />
                      </div>

                      {showConnectivitySpecificPrice && catalogItemId && (
                        <SpecificPriceInlineForm
                          type="connectivity"
                          accountId={accountId}
                          productId={catalogItemId}
                          negotiationType={negotiationType as NegotiationType}
                          proposalId={proposalId}
                          fallbackPriceCashInCents={selectedCatalogItem?.basePriceCashInCents ?? 0}
                          fallbackPriceInstallmentInCents={
                            selectedCatalogItem?.basePriceInstallmentInCents ?? 0
                          }
                          onClose={() => setShowConnectivitySpecificPrice(false)}
                        />
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            <FormError error={formError} onDismiss={clearFormError} />
            <div className="flex justify-end gap-3 pt-2">
              {!isEditMode && (
                <Button
                  type="button"
                  className="bg-black text-white hover:bg-gray-800"
                  onClick={handleAddAndNew}
                  disabled={!hasNegotiationType || isSubmitting}
                >
                  Adicionar e Novo
                </Button>
              )}

              <Button
                type="submit"
                className="bg-black text-white hover:bg-gray-800"
                disabled={!hasNegotiationType || isSubmitting}
              >
                {isSubmitting
                  ? isEditMode
                    ? 'Salvando...'
                    : 'Adicionando...'
                  : isEditMode
                    ? 'Salvar'
                    : 'Adicionar'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
